import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Users,
  Target,
  TrendingUp,
  Zap,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Lightbulb,
  Award,
  MessageSquare,
  Brain,
  GraduationCap,
  Stethoscope,
  FlaskConical,
  Sparkles,
  FolderKanban,
  Shield,
  Scale,
  Calendar,
  BookOpen,
  Network,
  Rocket,
  Compass,
  ChevronLeft,
  ChevronRight,
  Minus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ImpactStats } from "@/components/public/ImpactStats";
import ContactSalesDialog from "@/components/landing/ContactSalesDialog";
import launchVideo from "@/assets/lanzamiento-lideria.mp4.asset.json";
import launchVideoPoster from "@/assets/lanzamiento-lideria-poster.jpg.asset.json";

import expertHarold from "@/assets/experts/harold.png";
import expertAndres from "@/assets/experts/andres.png";
import expertKeythleen from "@/assets/experts/keythleen.png";
import expertJulian from "@/assets/experts/julian.png";

const EnterprisePage = () => {
  const navigate = useNavigate();
  const [logoIndex, setLogoIndex] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);

  const pillars = [
    {
      icon: GraduationCap,
      title: "Aprendizaje",
      description: "Rutas, contenidos y experiencias para formar líderes en IA en toda la organización.",
      modules: [
        "Rutas certificadas (Principal y Especializadas)",
        "Contenidos: video, lectura, audio, infografías e interactivos",
        "Clases con LIA (asistente inteligente)",
        "Calendario académico y sesiones en vivo",
        "Foro de comunidad y Showcase de proyectos",
      ],
    },
    {
      icon: FlaskConical,
      title: "Innovación",
      description: "Del diagnóstico al prototipo: convierte oportunidades en soluciones reales con IA.",
      modules: [
        "Diagnóstico de Madurez en IA (personal y empresarial)",
        "Laboratorio de Retos con consultoría IA",
        "Asistente de Prototipado por tipo de solución",
        "Catálogo de Herramientas IA filtrado por madurez",
        "Tendencias web y radar de oportunidades",
      ],
    },
    {
      icon: FolderKanban,
      title: "Gestión",
      description: "Portafolio de iniciativas IA medible, evaluable y con ROI demostrable.",
      modules: [
        "Proyectos y portafolio de iniciativas IA",
        "Evaluaciones LIDER-IA (12 componentes · 5 dimensiones)",
        "Análisis de sinergias y consolidación",
        "Dashboards de ROI, BCR y Payback",
        "Mentorías y métricas de entregables",
      ],
    },
    {
      icon: Shield,
      title: "Gobernanza",
      description: "Marcos, riesgos y cambio organizacional para escalar la IA con responsabilidad.",
      modules: [
        "Centro de Excelencia (CoE) en IA",
        "Gestión de riesgos y controles",
        "Cumplimiento regulatorio",
        "Gestión del cambio y rediseño de roles",
        "Auditoría y trazabilidad",
      ],
    },
  ];

  const journey = [
    {
      icon: Stethoscope,
      step: "01",
      title: "Diagnóstico",
      participant: "Madurez IA personal",
      admin: "Diagnóstico empresarial (15 subdimensiones)",
    },
    {
      icon: BookOpen,
      step: "02",
      title: "Aprendizaje",
      participant: "Rutas, contenidos y clases LIA",
      admin: "Gestión de rutas y cohortes",
    },
    {
      icon: Lightbulb,
      step: "03",
      title: "Innovación",
      participant: "Laboratorio de Retos · Prototipado",
      admin: "Catálogo de herramientas y tendencias",
    },
    {
      icon: FolderKanban,
      step: "04",
      title: "Proyectos",
      participant: "Portafolio personal de iniciativas",
      admin: "Portafolio IA y evaluaciones LIDER-IA",
    },
    {
      icon: BarChart3,
      step: "05",
      title: "Evaluación & ROI",
      participant: "Métricas y entregables",
      admin: "Dashboards de ROI, sinergias y NPS",
    },
    {
      icon: Scale,
      step: "06",
      title: "Gobernanza",
      participant: "Cumplimiento y buenas prácticas",
      admin: "CoE, riesgos, cambio y rediseño de roles",
    },
  ];

  const planComparison = [
    { capability: "Acceso a contenidos y rutas certificadas públicas", pro: true, enterprise: true },
    { capability: "Diagnóstico de madurez personal", pro: true, enterprise: true },
    { capability: "Laboratorio de Retos y Asistente de Prototipado", pro: true, enterprise: true },
    { capability: "Foro y Showcase de proyectos", pro: true, enterprise: true },
    { capability: "Gestión de rutas de aprendizaje propias", pro: false, enterprise: true, highlight: true },
    { capability: "Calendario de rutas: sesiones en vivo y mentorías", pro: false, enterprise: true, highlight: true },
    { capability: "Cohortes y participantes con seguimiento", pro: false, enterprise: true },
    { capability: "Mentores propios y expertos asignados", pro: false, enterprise: true },
    { capability: "Diagnóstico empresarial y dashboards de portafolio", pro: false, enterprise: true },
    { capability: "ROI, sinergias, riesgos y CoE", pro: false, enterprise: true },
    { capability: "Rediseño de roles y gestión del cambio", pro: false, enterprise: true },
    { capability: "Auditoría, trazabilidad y multi-tenant", pro: false, enterprise: true },
  ];

  const experts = [
    { name: "Harold Combita", role: "CEO Vivetori · Creador LIDER IA", company: "+16 años en IA", img: expertHarold },
    { name: "Andrés Calderón", role: "Consultor IA", company: "", img: expertAndres },
    { name: "Keythleen Castellar", role: "Consultora IA", company: "", img: expertKeythleen },
    { name: "Julián", role: "Consultor IA", company: "", img: expertJulian },
  ];

  const companyLogos = ["BN", "TI", "GR", "SP", "LE", "MS", "AC", "DT"];

  const qualitativeStats = [
    { value: "40%", title: "Reducción de costos", description: "Empresas reportan hasta 40% de reducción en costos operativos al adoptar IA con método." },
    { value: "65%", title: "Más productividad", description: "Incremento promedio en la productividad de equipos formados en la plataforma." },
    { value: "85%", title: "Satisfacción de cliente", description: "Mejora en métricas de experiencia del cliente en empresas participantes." },
  ];

  const testimonials = [
    {
      quote: "Líder IA nos dio una plataforma completa: dejamos de ver formación aislada y empezamos a gestionar IA como portafolio estratégico.",
      author: "María González",
      role: "CTO",
      company: "TechCorp Colombia",
    },
    {
      quote: "El diagnóstico, los proyectos y los dashboards de ROI nos permitieron justificar y escalar las iniciativas con el comité directivo.",
      author: "Carlos Rodríguez",
      role: "Director de Innovación",
      company: "Grupo Industrial SA",
    },
  ];

  const faqs = [
    {
      question: "¿Qué es exactamente Líder IA?",
      answer: "Es una plataforma integral de Aprendizaje, Innovación, Gestión y Gobernanza de IA. No es un curso ni un programa puntual: es el ecosistema operativo para que tu empresa lidere la IA de forma continua.",
    },
    {
      question: "¿En qué se diferencia el Plan Empresarial del Plan Pro?",
      answer: "El Plan Pro está pensado para profesionales individuales. El Plan Empresarial añade gestión de rutas de aprendizaje propias, calendario de sesiones en vivo y mentorías, cohortes, mentores propios y dashboards empresariales (ROI, sinergias, riesgos, CoE).",
    },
    {
      question: "¿Qué tamaño de empresa necesito tener?",
      answer: "Trabajamos con organizaciones desde 20 hasta más de 5.000 empleados. La plataforma es multi-tenant y se adapta al tamaño y madurez de cada cliente.",
    },
    {
      question: "¿Necesitamos un equipo técnico para usarla?",
      answer: "No. La plataforma está diseñada tanto para perfiles de negocio como técnicos. Los asistentes IA y el catálogo de herramientas se adaptan al nivel de madurez de cada usuario.",
    },
  ];

  const scrollLogos = (direction: "left" | "right") => {
    if (direction === "left") setLogoIndex((p) => Math.max(0, p - 1));
    else setLogoIndex((p) => Math.min(companyLogos.length - 6, p + 1));
  };

  return (
    <div className="space-y-12 pb-12">
      <SEO
        title="LÍDER IA para empresas — Transformación con IA"
        description="Soluciones de IA para empresas: diagnóstico, capacitación, gobernanza y proyectos con impacto medible."
        path="/empresas"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }}
      />
      {/* Hero */}
      <section className="py-12 px-6 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto relative space-y-12">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6 px-4 py-2">
              <Building2 className="h-4 w-4 mr-2" />
              Plataforma Empresarial
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              La plataforma integral para{" "}
              <span className="bg-gradient-to-r from-primary via-primary/80 to-accent bg-clip-text text-transparent">
                liderar la IA en tu empresa
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Aprendizaje · Innovación · Gestión · Gobernanza. Todo lo que tu organización necesita para
              adoptar y escalar la Inteligencia Artificial, en un solo ecosistema.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setContactOpen(true)} className="text-lg px-8">
                Solicitar Demo Empresarial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" onClick={() => setContactOpen(true)}>
                Conocer Planes
              </Button>
            </div>
          </div>

          <ImpactStats />
        </div>
      </section>

      {/* Video de lanzamiento */}
      <section className="py-16 px-6">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4">¿Qué es LÍDER IA?</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 italic">
            Conoce LÍDER IA en menos de 2 minutos
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            En menos de dos minutos te explicamos qué es la plataforma y cómo ayuda a tu empresa
            a liderar la IA de principio a fin.
          </p>
        </div>
        <Card className="max-w-5xl mx-auto overflow-hidden bg-background border-border/50">
          <CardContent className="p-0">
            <video
              src={launchVideo.url}
              poster={launchVideoPoster.url}
              controls
              preload="metadata"
              playsInline
              className="w-full aspect-video"
            />
          </CardContent>
        </Card>
      </section>

      {/* 4 Pilares */}
      <section className="py-16 px-6 rounded-2xl bg-muted/30 border border-border">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Plataforma</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 italic">
            Los 4 pilares de Líder IA
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Una plataforma única que integra todos los módulos que tu empresa necesita para
            convertir la IA en resultados.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {pillars.map((pillar, i) => (
            <Card key={i} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 bg-background">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <pillar.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">{pillar.title}</h3>
                </div>
                <p className="text-muted-foreground mb-5">{pillar.description}</p>
                <ul className="space-y-2">
                  {pillar.modules.map((m, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-sm">{m}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Journey */}
      <section className="py-16 px-6">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Recorrido</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 italic">
            Un recorrido end-to-end por la plataforma
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Cada etapa conecta la experiencia del participante con los módulos de gestión del
            administrador, manteniendo trazabilidad completa.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {journey.map((j, i) => (
            <Card key={i} className="relative overflow-hidden hover:shadow-lg transition-all border-border/50">
              <div className="absolute top-0 right-0 text-6xl font-bold text-primary/5 px-4 leading-none">
                {j.step}
              </div>
              <CardContent className="p-6 relative">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <j.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{j.title}</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold text-primary">Participante: </span>
                    <span className="text-muted-foreground">{j.participant}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-accent-foreground">Admin: </span>
                    <span className="text-muted-foreground">{j.admin}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Plan Empresarial vs Pro */}
      <section className="py-16 px-6 rounded-2xl bg-gradient-to-br from-primary/5 via-background to-accent/5 border border-border">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4">Comparativa</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 italic">
            Plan Empresarial vs Plan Pro
          </h2>
          <p className="text-xl text-muted-foreground">
            Las empresas pueden crear y administrar sus propias <strong>rutas</strong>, <strong>cohortes</strong> y
            la <strong>agenda de sesiones en vivo y mentorías</strong>. El Plan Pro está pensado para profesionales individuales.
          </p>
        </div>

        <Card className="max-w-5xl mx-auto overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-4 font-semibold">Capacidad</th>
                  <th className="p-4 font-semibold text-center w-32">Plan Pro</th>
                  <th className="p-4 font-semibold text-center w-40 bg-primary/10 text-primary">
                    Plan Empresarial
                  </th>
                </tr>
              </thead>
              <tbody>
                {planComparison.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t border-border ${row.highlight ? "bg-primary/5" : ""}`}
                  >
                    <td className="p-4 text-sm">
                      {row.highlight ? <strong>{row.capability}</strong> : row.capability}
                    </td>
                    <td className="p-4 text-center">
                      {row.pro ? (
                        <CheckCircle className="h-5 w-5 text-primary mx-auto" />
                      ) : (
                        <Minus className="h-5 w-5 text-muted-foreground/50 mx-auto" />
                      )}
                    </td>
                    <td className="p-4 text-center bg-primary/5">
                      {row.enterprise ? (
                        <CheckCircle className="h-5 w-5 text-primary mx-auto" />
                      ) : (
                        <Minus className="h-5 w-5 text-muted-foreground/50 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Stats cualitativos + Nuestro Impacto */}
      <section className="py-16 px-6 rounded-2xl bg-muted/30 border border-border">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {qualitativeStats.map((stat, i) => (
            <Card key={i} className="p-6 bg-background">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.value}</div>
              <h3 className="font-semibold text-lg mb-2">{stat.title}</h3>
              <p className="text-muted-foreground text-sm">{stat.description}</p>
            </Card>
          ))}
        </div>

        <div className="mt-16 max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <TrendingUp className="h-4 w-4 mr-2" />
              Nuestro Impacto
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold italic">
              Resultados que respaldan nuestra trayectoria
            </h2>
          </div>
          <ImpactStats />
        </div>
      </section>

      {/* Expertos */}
      <section className="py-16 px-6">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Expertos</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 italic">
            Aprende de líderes de la industria
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Mentores y consultores con experiencia real implementando IA en organizaciones de Latinoamérica.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {experts.map((expert, i) => (
            <Card key={i} className="group hover:shadow-lg transition-all border-border/50 hover:border-primary/30 bg-background text-center">
              <CardContent className="p-6">
                <img
                  src={expert.img}
                  alt={expert.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg font-semibold">{expert.name}</h3>
                <p className="text-sm text-muted-foreground">{expert.role}</p>
                {expert.company && <p className="text-sm text-muted-foreground">{expert.company}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-muted/30 rounded-2xl">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">Casos de éxito</Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Lo que dicen nuestros clientes</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="h-5 w-5 text-yellow-500">★</div>
                  ))}
                </div>
                <p className="text-lg italic mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{t.author}</div>
                    <div className="text-sm text-muted-foreground">{t.role}, {t.company}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Preguntas frecuentes</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <Card key={i} className="p-6">
                <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10 rounded-2xl">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-6 px-4 py-2">
            <Zap className="h-4 w-4 mr-2" />
            Comienza Hoy
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Lleva la plataforma Líder IA a tu empresa
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Aprendizaje, innovación, gestión y gobernanza de IA en un único ecosistema.
            Agenda una demo y conoce cómo escalar IA con resultados medibles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate("/auth?mode=signup")} className="text-lg px-8">
              Solicitar Demo Empresarial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8" onClick={() => setContactOpen(true)}>
              <MessageSquare className="mr-2 h-5 w-5" />
              Contactar Ventas
            </Button>
          </div>
        </div>
      </section>
      <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};

export default EnterprisePage;
